import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import CountryTable from '../components/CountryTable';
import { useDeleteCountry } from '../hooks/useCountryMutations';
import type { Country } from '@/shared/types';
import DynamicFilterBuilder from '@/shared/filtering/DynamicFilterBuilder';
import { ADMIN_FILTER_RESOURCES } from '@/shared/filtering/types';
import { useDynamicAdminList } from '@/shared/filtering/useDynamicAdminList';

export default function CountryListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState<Country | null>(null);

  const list = useDynamicAdminList<Country>(ADMIN_FILTER_RESOURCES.countries, {
    initialSortField: 'id',
    initialSortDir: 'asc',
  });
  const { data, isLoading } = list.searchQuery;
  const deleteMutation = useDeleteCountry();

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, { onSettled: () => setDeleteTarget(null) });
  };

  return (
    <div>
      <PageHeader title={t('countries.title')} actionLabel={t('countries.addNew')} onAction={() => navigate('/countries/create')} />
      <DynamicFilterBuilder resource={list.resource} metadata={list.metadataQuery.data} value={list.filters} onChange={list.setFilters} onReset={list.resetFilters} />
      <div style={{ marginTop: 16 }}>
        <CountryTable
          data={data?.content || []}
          loading={isLoading}
          sortField={list.sortField}
          sortDir={list.sortDir}
          onSort={list.handleSort}
          onEdit={(id) => navigate(`/countries/${id}/edit`)}
          onDelete={(item) => setDeleteTarget(item)}
          page={data?.currentPage ?? 0}
          pageSize={list.size}
          total={data?.totalItems ?? 0}
          onPageChange={list.setPage}
        />
      </div>
      <ConfirmDialog
        open={!!deleteTarget}
        title={t('countries.deleteTitle')}
        message={`${t('countries.deleteMessage')} "${deleteTarget?.nameEn}"?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
